import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Siren, Bell, MapPin, Contact, Users, Zap, Timer, Navigation, ArrowRight, UsersRound,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { SectionReveal, StaggerGroup, StaggerItem } from "@/components/shared/SectionReveal";
import { DownloadButton } from "@/components/shared/DownloadButton";

const ICONS = [Siren, Bell, MapPin, Contact, Users, Zap, Timer, Navigation, UsersRound];

export default function Features() {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const list = t("features.list", { returnObjects: true });

  return (
    <div data-testid="features-page">
      <SEO title={`${t("features.hero_title")} — Fastority`} description={t("features.hero_subtitle")} path="features" />

      <section className="bg-deepblue text-white py-24 sm:py-32 grain-overlay relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,90,95,0.15),transparent_55%)]" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter"
            data-testid="features-hero-title"
          >
            {t("features.hero_title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-lg text-white/70"
          >
            {t("features.hero_subtitle")}
          </motion.p>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-offwhite">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionReveal className="text-center mb-10">
            <span className="inline-block text-sm font-bold uppercase tracking-[0.15em] text-deepblue bg-deepblue/10 px-4 py-1.5 rounded-full" data-testid="features-free-title">
              {t("features.free_title")}
            </span>
          </SectionReveal>
          <StaggerGroup className="flex flex-wrap justify-center gap-6 mb-20">
            {list.slice(0, 5).map((item, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <StaggerItem key={item.title} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
                  <div
                    data-testid={`feature-card-${i}`}
                    className="bg-white rounded-3xl p-8 h-full shadow-[0_8px_30px_rgba(30,58,138,0.08)] border border-gray-100 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(255,90,95,0.15)] transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6 bg-coral/10">
                      <Icon className="text-coral" size={22} strokeWidth={2.2} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>

          <SectionReveal className="text-center mb-10">
            <span className="inline-block text-sm font-bold uppercase tracking-[0.15em] text-coral bg-deepblue px-4 py-1.5 rounded-full" data-testid="features-premium-title">
              {t("features.premium_title")}
            </span>
          </SectionReveal>
          <StaggerGroup className="flex flex-wrap justify-center gap-6">
            {list.slice(5, 9).map((item, idx) => {
              const i = idx + 5;
              const Icon = ICONS[i % ICONS.length];
              return (
                <StaggerItem key={item.title} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
                  <div
                    data-testid={`feature-card-${i}`}
                    className="bg-white rounded-3xl p-8 h-full shadow-[0_8px_30px_rgba(30,58,138,0.08)] border border-gray-100 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(255,90,95,0.15)] transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6 bg-deepblue">
                      <Icon className="text-coral" size={22} strokeWidth={2.2} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate900 mb-2">
                      {item.title}
                      <span className="ml-2 text-sm font-bold text-coral align-middle" data-testid={`feature-premium-badge-${i}`}>(Premium)</span>
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      <section className="py-24 bg-white text-center">
        <SectionReveal className="max-w-2xl mx-auto px-5">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate900">{t("features.cta_title")}</h2>
          <p className="mt-4 text-lg text-slate-600">{t("features.cta_subtitle")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <DownloadButton testId="features-cta-download" className="inline-flex items-center gap-2 bg-coral text-white font-semibold px-7 py-4 rounded-full hover:scale-[1.02] transition-transform duration-300">
              {t("home.hero.cta_primary")} <ArrowRight size={18} />
            </DownloadButton>
            <Link to={`/${lang}/pricing`} data-testid="features-cta-pricing" className="inline-flex items-center gap-2 text-slate900 font-medium px-6 py-4 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors">
              {t("nav.pricing")}
            </Link>
          </div>
        </SectionReveal>
      </section>
    </div>
  );
}
